import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, getStoredAttribution } from "@/lib/analytics";

interface SignUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SignUpDialog = ({ open, onOpenChange }: SignUpDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) trackEvent("signup_dialog_opened");
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) return;
    setLoading(true);
    const { error } = await supabase.from("signups").insert({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      organization: org.trim() || null,
    });
    setLoading(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("You're already signed up — we'll be in touch!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      return;
    }
    trackEvent("signup_submitted", {
      has_organization: Boolean(org.trim()),
      ...getStoredAttribution(),
    });
    toast.success("Welcome aboard! We'll be in touch soon.");
    onOpenChange(false);
    setName("");
    setEmail("");
    setOrg("");
    navigate("/");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">Start a Project with The Emerging Market</DialogTitle>
          <DialogDescription>Join Alabama's policy and data community. Sign up to access legislation tracking, grant opportunities, and more.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Input
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-background border-border"
            />
          </div>
          <div>
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-background border-border"
            />
          </div>
          <div>
            <Input
              placeholder="Organization (optional)"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              className="bg-background border-border"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display font-semibold shadow-glow"
          >
            {loading ? "Signing up…" : "Sign Up"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SignUpDialog;
